import { useState } from 'react';
import { arrayOf, bool, shape, string } from 'prop-types';
import { useDispatch } from 'react-redux';
import { createInstantCartForBoxSelections } from '../../../../../modules/growth';
import { processBoxSubscription } from '../../../../../utils/data';
import env from '../../../../../utils/env';
import { navigateTo } from '../../../../../utils/location';
import { openMauiInstantCheckout } from '../../../../../utils/openMauiInstantCheckout';
import * as styles from '../../BoxSubscriptionPanel.module.css';

const ActionCta = ({
  hasPartialSelections,
  hasAdditionalPricing,
  boxSlug,
  isOpen,
  isProcessBoxEnabled,
  userExistingCart,
  boxSubscription,
  sortedProducts,
  isBeautyBox = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCheckoutNowLoading, setIsCheckoutNowLoading] = useState(false);
  const dispatch = useDispatch();

  const handleFinishCustomizing = () => {
    navigateTo(`${env.envURL}/customize-box${boxSlug ? `/${boxSlug}` : ''}`);
  };

  const handleTrackOrder = () => {
    // TODO: Investigate where to get the tracking link from
    navigateTo(`${env.envURL}/my-account/orders`);
  };

  const handleCheckoutNow = async () => {
    if (isCheckoutNowLoading) return;
    setIsCheckoutNowLoading(true);
    setError(null);

    const selections = (sortedProducts ?? [])
      .filter((item) => item.choiceProductUuid !== null)
      .map((item) => ({
        productUuid: item.choiceProductUuid,
        saleUuid: item.saleUuid,
        catalogUuid: item.catalogUuid,
        choiceType: item.choiceType,
        quantity: item.choiceQuantity ?? 1,
      }));

    try {
      const cartUuid = await dispatch(createInstantCartForBoxSelections({ selections }));
      if (!cartUuid) {
        // Thunk already dispatched failure + toast. Reset loading and stay on the panel.
        setIsCheckoutNowLoading(false);
        return;
      }
      openMauiInstantCheckout(cartUuid);
      // Intentionally NOT clearing isCheckoutNowLoading — the browser is about
      // to navigate away, so leaving the button in its loading state prevents
      // a double-click before the redirect completes.
    } catch (err) {
      console.error('Error creating instant cart:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setIsCheckoutNowLoading(false);
    }
  };

  const handleProcessBox = async () => {
    if (hasActiveCart && boxSubscription?.customizeSaleId && sortedProducts?.length > 0) {
      setIsLoading(true);
      setError(null);

      const cartItemsToProcess = sortedProducts
        .filter((item) => item.choiceProductUuid !== null)
        .map((item) => ({
          saleUuid: item.saleUuid,
          catalogUuid: item.catalogUuid,
          productUuid: item.choiceProductUuid,
          quantity: item.choiceQuantity ?? 1,
          cartItemPreferences: {
            CUSTOMIZE_CHOICE_TYPE: item.choiceType ?? 'PRIMARY',
          },
        }));

      try {
        const result = await processBoxSubscription(
          userExistingCart.uuid,
          boxSubscription.customizeSaleId,
          cartItemsToProcess,
        );

        if (result && result?.error?.errors?.length > 0) {
          throw new Error('Failed to process box');
        }

        // TODO: Verify logic after sucessful checkout
        window.location.reload();
      } catch (err) {
        console.error('Error processing box:', err);
        setError(err.message || 'Failed to process box. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (hasPartialSelections && isOpen) {
    return (
      <div className={styles.summaryDetails}>
        <button className={styles.actionCtaButton} onClick={handleFinishCustomizing}>
          Finish Customizing
        </button>
      </div>
    );
  }

  if (!isOpen) {
    return (
      <div className={styles.summaryDetails}>
        <button className={styles.actionCtaButton} onClick={handleTrackOrder}>
          Track Your Order
        </button>
      </div>
    );
  }

  // Beauty edit box with any paid selection (ADDITIONAL or luxe PRIMARY — both
  // surface via `hasAdditionalPricing`) → route the user through maui's instant
  // checkout instead of the legacy Process Box / no-op paths.
  if (isBeautyBox && hasAdditionalPricing) {
    return (
      <div className={styles.summaryDetails}>
        {error && <div className={styles.errorMessage}>{error}</div>}
        <button
          className={styles.actionCtaButton}
          onClick={handleCheckoutNow}
          disabled={isCheckoutNowLoading}
        >
          {isCheckoutNowLoading ? 'Processing...' : 'Checkout Now'}
        </button>
      </div>
    );
  }

  if (!isProcessBoxEnabled || hasAdditionalPricing) {
    return null;
  }

  //TODO: MORE STATES WILL BE ADDED IN SEPARATE TICKETS.
  const hasActiveCart = Object.keys(userExistingCart ?? {}).length > 0;

  return (
    <div className={styles.summaryDetails}>
      {error && <div className={styles.errorMessage}>{error}</div>}
      <button
        className={styles.actionCtaButton}
        onClick={handleProcessBox}
        disabled={!hasActiveCart || isLoading}
      >
        {isLoading ? 'Processing...' : 'Process Box Now'}
      </button>
      <p className={styles.checkoutDescription}>
        Don&apos;t want to wait? Process your box now to get your box earlier.
      </p>
    </div>
  );
};

ActionCta.propTypes = {
  hasPartialSelections: bool.isRequired,
  hasAdditionalPricing: bool,
  boxSlug: string.isRequired,
  isOpen: bool.isRequired,
  isProcessBoxEnabled: bool.isRequired,
  userExistingCart: shape({}),
  boxSubscription: shape({}),
  sortedProducts: arrayOf(shape({})),
  isBeautyBox: bool,
};

export default ActionCta;
